// The web app inside the Mac app, "Corvus Agent Platform" (mac/). The Mac
// window has no title bar: the web view covers it edge to edge, macOS
// draws its three window buttons over the top-left corner, and the
// dashboard's top row (the sidebar's first row and the top bar, 56px)
// stands in for the title bar: the sidebar toggle and back / forward sit
// in it, and its empty space drags the window. This module is the web
// side of that: whether this page runs in the Mac app, and the protocol
// both sides share (Part A of the Mac shell spec, 14 Oct 2026). Names and
// shapes here are the contract with the Mac app: do not change them.
//
// In the Mac app iff the user agent carries the app's token (" Corvus/",
// from DesktopDevice.userAgentSuffix: "… Safari/605.1.15 Corvus/<version>")
// AND the app's message handler, `corvusDesktop`, is on the page (the app
// registers it on its main window before any page loads). Never the
// iPhone app: its handler is `lattice` and its token "CorvusiOS"
// (native/embedded.ts), and an embedded page is never the Mac shell.
//
// On detection <html data-shell="mac">: set before first paint by the tiny
// inline script in index.html (the same two checks), confirmed here at
// boot (main.tsx, after initEmbedded). Everything the Mac shell draws keys
// off it; in a browser and in the iPhone app nothing here runs and the
// dashboard is exactly what it was.
//
// Web → app: window.webkit.messageHandlers.corvusDesktop.postMessage(obj)
//   ready        {type, version: 1}: once, when the page can take calls
//   history      {type, canGoBack, canGoForward}: first, then on change
//   theme        {type, name, background: "#rrggbb"}: on mount and change
//   dragRegions  {type, drag: [[x, y, w, h]…], noDrag: […]}: CSS px from
//                the viewport's top-left (getBoundingClientRect), whole
//                pixels, every box outward to the pixel grid. drag = each
//                [data-drag-row]; noDrag = every interactive element in
//                one (NO_DRAG_SELECTOR), clipped to its row, and every
//                span of a row that something else is drawn over (a
//                dialog, a drawer, a toast: coveredSpans). One message
//                per animation frame at most, only when it changed; also
//                re-measured when a portal opens or closes and while the
//                pointer moves in the row, so it is current at a click.
//   recording    {type, active, phase, startedAt, title, unsent, stored, held, storage,
//                clientRef, meetingId, bound, flushed}: on every recorder change (desktopRecording.ts).
// App → web: window.corvusDesktop.{back, forward, toggleSidebar, setFullScreen(on),
//   stopRecording(), flushRecording()} by evaluateJavaScript (the two recorder
//   calls: native/desktopRecording.ts).
//
// The pure parts are self-tested (desktop.selftest.ts, run by
// `npm run check:format`).

import { getTheme } from "@/theme/themes";
import type { RecordingMessage } from "./desktopRecording";

export const DESKTOP_HANDLER = "corvusDesktop";
/** The Mac app's user-agent token (with its leading space: never "CorvusiOS"). */
export const DESKTOP_UA_TOKEN = " Corvus/";
/** `ready`'s version: 1 = the messages and calls listed above. */
export const DESKTOP_CONTRACT_VERSION = 1;
/** The attribute every drag row carries (CustomerSidebar, CustomerHeader). */
export const DRAG_ROW_ATTR = "data-drag-row";
/**
 * What stays clickable inside a drag row. A disabled button too: a click
 * on a dimmed arrow must not start a window drag either.
 */
export const NO_DRAG_SELECTOR = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  "summary",
  "[role='button']",
  "[role='tab']",
  "[role='link']",
  "[role='menuitem']",
  "[role='combobox']",
  "[role='switch']",
  "[role='checkbox']",
  "[contenteditable]",
  "[data-no-drag]",
].join(", ");

/** [x, y, width, height] in CSS px from the viewport's top-left. */
export type Box = [number, number, number, number];

export interface DragRegions {
  drag: Box[];
  noDrag: Box[];
}

export interface HistoryFlags {
  canGoBack: boolean;
  canGoForward: boolean;
}

export type DesktopMessage =
  | { type: "ready"; version: number }
  | ({ type: "dragRegions" } & DragRegions)
  | { type: "theme"; name: string; background: string }
  | ({ type: "history" } & HistoryFlags)
  | RecordingMessage;

/** What the Mac app calls on the page (window.corvusDesktop). */
export interface DesktopApi {
  back: () => void;
  forward: () => void;
  toggleSidebar: () => void;
  setFullScreen: (on: boolean) => void;
  stopRecording: () => void;
  flushRecording: () => void;
}

interface MessageHandler {
  postMessage: (message: unknown) => void;
}

// ─── detection ───

/** Whether a user agent is the Mac app's. */
export function hasDesktopToken(userAgent: string | null | undefined): boolean {
  return typeof userAgent === "string" && userAgent.includes(DESKTOP_UA_TOKEN);
}

/** The Mac app's message handler on a global scope, or null anywhere else. */
export function desktopHandler(scope: unknown): MessageHandler | null {
  const webkit = (scope as { webkit?: { messageHandlers?: Record<string, unknown> } } | null)?.webkit;
  const handler = webkit?.messageHandlers?.[DESKTOP_HANDLER] as Partial<MessageHandler> | undefined;
  return handler && typeof handler.postMessage === "function" ? (handler as MessageHandler) : null;
}

/** In the Mac app: its token AND its handler, and never a web screen of the iPhone app. */
export function resolveMacShell(userAgent: string | null | undefined, handlerPresent: boolean, embedded: boolean): boolean {
  return !embedded && handlerPresent && hasDesktopToken(userAgent);
}

let mac: boolean | undefined;

/**
 * Decide once, at boot (main.tsx, after initEmbedded and before React
 * renders): set or clear <html data-shell="mac"> and, in the Mac app,
 * give it window.corvusDesktop. `forced` is the development build's
 * `?shell=mac` (native/desktopDev.ts); production never passes it.
 */
export function initDesktop(embedded: boolean, forced = false): boolean {
  if (mac !== undefined) return mac;
  if (typeof window === "undefined") return (mac = false);
  mac = forced || resolveMacShell(window.navigator.userAgent, desktopHandler(window) !== null, embedded);
  const el = document.documentElement;
  if (mac) {
    el.setAttribute("data-shell", "mac");
    (window as Window & { corvusDesktop?: DesktopApi }).corvusDesktop = {
      back: goBack,
      forward: goForward,
      toggleSidebar: () => sidebarToggler?.(),
      setFullScreen,
      stopRecording: () => recordingControls?.stop(),
      flushRecording: () => recordingControls?.flush(),
    };
  } else if (el.getAttribute("data-shell") === "mac") {
    // The inline script and this module disagree (an embedded page): this wins.
    el.removeAttribute("data-shell");
  }
  return mac;
}

/** True when this page is the Mac app's window. */
export function isMacShell(): boolean {
  return mac === true;
}

/** Send a message to the Mac app; a no-op (false) anywhere else. */
export function postToDesktop(message: DesktopMessage): boolean {
  if (!mac) return false;
  const handler = desktopHandler(window);
  if (!handler) return false;
  try {
    handler.postMessage(message);
    return true;
  } catch {
    return false;
  }
}

// ─── full screen ───

/** The app's will-enter / did-exit full screen: no room kept for the window buttons. */
export function setFullScreen(on: boolean): void {
  document.documentElement.toggleAttribute("data-fullscreen", on === true);
  scheduleRegions();
}

// ─── the sidebar ───

let sidebarToggler: (() => void) | null = null;

/** The workspace shell says how to fold its sidebar (null when it unmounts). */
export function setSidebarToggler(fn: (() => void) | null): void {
  sidebarToggler = fn;
}

// ─── the meeting recorder ───

export interface RecordingControls { stop: () => void; flush: () => void }
let recordingControls: RecordingControls | null = null;
/** How to stop the recorder and store what it captured (RecorderLifecycle; null when it goes). */
export function setRecordingControls(c: RecordingControls | null): void {
  recordingControls = c;
}

// ─── back / forward ───
//
// Within this window's dashboard only. react-router's BrowserRouter keeps
// its own entry index in history.state.idx (0 = the first dashboard page
// of this window); going back from 0 would reach the sign-in hop, so it
// never does. Forward: the highest index reached, kept here; a PUSH
// drops what was ahead (max = idx), a POP or REPLACE keeps it.

export type NavigationType = "PUSH" | "POP" | "REPLACE";

/** The next highest index reached, after a navigation of `type` to `idx`. */
export function nextMaxIdx(type: NavigationType, idx: number, max: number | null): number {
  if (max === null || type === "PUSH") return idx;
  return Math.max(max, idx);
}

export function historyFlags(idx: number, max: number): HistoryFlags {
  return { canGoBack: idx > 0, canGoForward: idx < max };
}

/** react-router's entry index from a history.state value; 0 when it has none. */
export function routerIdx(state: unknown): number {
  const idx = (state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" && Number.isInteger(idx) && idx >= 0 ? idx : 0;
}

let maxIdx: number | null = null;
let flags: HistoryFlags = { canGoBack: false, canGoForward: false };
let flagsSent = false;
const flagListeners = new Set<() => void>();

/** Called on every location change (DesktopHistoryBridge, inside the router). */
export function noteNavigation(type: NavigationType): void {
  const idx = routerIdx(window.history.state);
  maxIdx = nextMaxIdx(type, idx, maxIdx);
  const next = historyFlags(idx, maxIdx);
  const changed = next.canGoBack !== flags.canGoBack || next.canGoForward !== flags.canGoForward;
  if (changed) {
    flags = next;
    flagListeners.forEach((fn) => fn());
  }
  if (changed || !flagsSent) {
    flagsSent = true;
    postToDesktop({ type: "history", ...flags });
  }
  scheduleRegions();
}

export function subscribeHistoryFlags(fn: () => void): () => void {
  flagListeners.add(fn);
  return () => flagListeners.delete(fn);
}

export function getHistoryFlags(): HistoryFlags {
  return flags;
}

export function goBack(): void {
  if (routerIdx(window.history.state) > 0) window.history.back();
}

export function goForward(): void {
  if (flags.canGoForward) window.history.forward();
}

// ─── ready ───

let readySent = false;

/** `ready`, once per page load. */
export function announceReady(): void {
  if (readySent) return;
  readySent = postToDesktop({ type: "ready", version: DESKTOP_CONTRACT_VERSION });
}

// ─── theme ───

let lastTheme: string | null = null;

/** `theme` for this theme name, unless the app was already told exactly that. */
export function announceTheme(name: string | null | undefined): void {
  const message = themeMessage(name);
  const key = `${message.name} ${message.background}`;
  if (key === lastTheme) return;
  if (postToDesktop(message)) lastTheme = key;
}

/** "#rrggbb" in lower case, or null for anything else (#rgb is widened). */
export function hexColour(value: string | null | undefined): string | null {
  const v = (value ?? "").trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(v)) return v;
  if (/^#[0-9a-f]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  return null;
}

/** The `theme` message for a theme name (an unknown name is the default theme, as everywhere). */
export function themeMessage(name: string | null | undefined): Extract<DesktopMessage, { type: "theme" }> {
  const theme = getTheme(name);
  const fallback = getTheme(null);
  return {
    type: "theme",
    name: theme.name,
    // Every registered theme's bg is #rrggbb (desktop.selftest.ts pins them).
    background: hexColour(theme.color.bg) ?? hexColour(fallback.color.bg) ?? fallback.color.bg,
  };
}

// ─── drag regions ───

interface RectLike {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** A rectangle widened outward to whole pixels, or null when it is empty. */
export function boxOf(r: RectLike): Box | null {
  if (![r.left, r.top, r.right, r.bottom].every(Number.isFinite)) return null;
  const x = Math.floor(r.left);
  const y = Math.floor(r.top);
  const w = Math.ceil(r.right) - x;
  const h = Math.ceil(r.bottom) - y;
  return w > 0 && h > 0 ? [x, y, w, h] : null;
}

/** The part of `inner` inside `outer`, or null when they do not overlap. */
export function clipBox(inner: Box, outer: Box): Box | null {
  const x = Math.max(inner[0], outer[0]);
  const y = Math.max(inner[1], outer[1]);
  const right = Math.min(inner[0] + inner[2], outer[0] + outer[2]);
  const bottom = Math.min(inner[1] + inner[3], outer[1] + outer[3]);
  return right > x && bottom > y ? [x, y, right - x, bottom - y] : null;
}

/** How far apart the hit tests along a drag row are, in CSS px. */
export const COVER_STEP = 16;

/** What a click at a point would reach: the row itself (or something in it), something else, or nothing. */
export type TopAt = (x: number, y: number) => "row" | "other" | null;

/**
 * The spans of a drag row that something else is drawn over: a dialog's
 * scrim, a drawer whose close button sits up there, a toast, the bell's
 * click-catcher, the recorder's dimmed screen. A click there belongs to
 * that, not to the window, so each span is noDrag: the row's full height,
 * found by hit tests every COVER_STEP px along three lines, widened by a
 * step each side (clickable beats draggable when in doubt).
 */
export function coveredSpans(row: Box, topAt: TopAt, step = COVER_STEP): Box[] {
  const [rx, ry, rw, rh] = row;
  const ys = [ry + Math.min(8, rh / 4), ry + rh / 2, ry + rh - Math.min(8, rh / 4)];
  const xs: number[] = [];
  for (let x = rx + step / 2; x < rx + rw; x += step) xs.push(x);
  const spans: Box[] = [];
  let start = -1;
  for (let i = 0; i <= xs.length; i++) {
    if (i < xs.length && ys.some((y) => topAt(xs[i], y) === "other")) {
      if (start < 0) start = i;
      continue;
    }
    if (start < 0) continue;
    const left = Math.max(rx, Math.floor(xs[start] - step));
    const right = Math.min(rx + rw, Math.ceil(xs[i - 1] + step));
    spans.push([left, ry, right - left, rh]);
    start = -1;
  }
  return spans;
}

/**
 * The regions of the drag rows in `root`: each row, what is clickable in
 * it, and (with `hitTest`, the live page's) what is drawn over it.
 */
export function collectRegions(root: ParentNode, hitTest?: (row: Element) => TopAt): DragRegions {
  const drag: Box[] = [];
  const noDrag: Box[] = [];
  root.querySelectorAll(`[${DRAG_ROW_ATTR}]`).forEach((row) => {
    const box = boxOf(row.getBoundingClientRect());
    if (!box) return;
    drag.push(box);
    row.querySelectorAll(NO_DRAG_SELECTOR).forEach((el) => {
      const inner = boxOf(el.getBoundingClientRect());
      const clipped = inner && clipBox(inner, box);
      if (clipped) noDrag.push(clipped);
    });
    if (hitTest) noDrag.push(...coveredSpans(box, hitTest(row)));
  });
  return { drag, noDrag };
}

/** What a click would reach on the live page (elementFromPoint ignores pointer-events: none). */
function liveHitTest(row: Element): TopAt {
  return (x, y) => {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    return row.contains(el) ? "row" : "other";
  };
}

/** A stable key for "did it change". */
export function regionsKey(regions: DragRegions): string {
  return JSON.stringify([regions.drag, regions.noDrag]);
}

let frame = 0;
let lastRegions: string | null = null;
let stripBottom = 0;
let watched: Element[] = [];
let resizeObserver: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;
let layerObserver: MutationObserver | null = null;
let pointerTimer = 0;
let watching = 0;

function flushRegions(): void {
  frame = 0;
  if (!mac) return;
  // Rows can be swapped by a re-render (the sidebar folding): follow them.
  const rows = Array.from(document.querySelectorAll(`[${DRAG_ROW_ATTR}]`));
  if (watching > 0 && (rows.length !== watched.length || rows.some((r, i) => r !== watched[i]))) observe(rows);
  const regions = watching > 0 ? collectRegions(document, liveHitTest) : { drag: [], noDrag: [] };
  stripBottom = Math.max(0, ...regions.drag.map((b) => b[1] + b[3]));
  const key = regionsKey(regions);
  if (key === lastRegions) return;
  lastRegions = key;
  postToDesktop({ type: "dragRegions", ...regions });
}

/** Ask for the regions to be measured again, at the next animation frame. */
export function scheduleRegions(): void {
  if (!mac || frame) return;
  frame = window.requestAnimationFrame(flushRegions);
}

const onWindowChange = () => scheduleRegions();

// The pointer in the top row: measure again (at most every 80ms) so that
// whatever is drawn over the row now is known before the click.
const onPointerMove = (e: MouseEvent) => {
  if (e.clientY > stripBottom || pointerTimer) return;
  pointerTimer = window.setTimeout(() => {
    pointerTimer = 0;
    scheduleRegions();
  }, 80);
};

function observe(rows: Element[]): void {
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
  watched.forEach((row) => row.removeEventListener("scroll", onWindowChange, true));
  watched = rows;
  resizeObserver ??= new ResizeObserver(onWindowChange);
  mutationObserver ??= new MutationObserver(onWindowChange);
  for (const row of rows) {
    resizeObserver.observe(row);
    // Content changes (a route's tabs, a count, the toggle moving) move
    // what is clickable without resizing the row.
    mutationObserver.observe(row, { childList: true, subtree: true, attributes: true, characterData: true });
    // A tab strip that scrolls sideways moves its tabs.
    row.addEventListener("scroll", onWindowChange, true);
  }
}

/**
 * Keep the app told while the workspace shell is on screen (CustomerShell).
 * Returns the stop function: the shell going away (another shell, the
 * sign-in page) sends empty regions, so the app drags nothing stale.
 */
export function watchDragRows(): () => void {
  if (!mac) return () => undefined;
  watching += 1;
  if (watching === 1) {
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("mousemove", onPointerMove, { passive: true });
    // Dialogs, drawers, menus and toasts are portals: children of <body>.
    layerObserver ??= new MutationObserver(onWindowChange);
    layerObserver.observe(document.body, { childList: true });
    document.fonts?.ready.then(onWindowChange).catch(() => undefined);
  }
  observe(Array.from(document.querySelectorAll(`[${DRAG_ROW_ATTR}]`)));
  scheduleRegions();
  return () => {
    watching -= 1;
    if (watching > 0) return;
    window.removeEventListener("resize", onWindowChange);
    window.removeEventListener("mousemove", onPointerMove);
    layerObserver?.disconnect();
    window.clearTimeout(pointerTimer);
    pointerTimer = 0;
    observe([]);
    scheduleRegions();
  };
}
